import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'

type Location = {
  id: string; code: string; type: string; zone: string
  capacity: number; active: boolean; warehouseId: string | null
  address: string | null; warehouseName?: string | null
}
type Warehouse = { id: string; name: string; type: string }

const typeLabel: Record<string, string> = { pallet: 'Palete', shelf: 'Prateleira', floor: 'Piso', staging: 'Staging' }
const zoneLabel: Record<string, string> = { external: 'Externo', internal: 'Interno' }

export function LocationsPage() {
  const qc = useQueryClient()
  const [createModal, setCreateModal] = useState(false)
  const [editModal, setEditModal] = useState<Location | null>(null)
  const [form, setForm] = useState({ code: '', type: 'pallet', zone: 'external', warehouseId: '', capacity: '1', address: '' })
  const [filterWarehouse, setFilterWarehouse] = useState('')

  const { data: locations = [], isLoading } = useQuery<Location[]>({
    queryKey: ['locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
  })

  const { data: warehouses = [] } = useQuery<Warehouse[]>({
    queryKey: ['warehouses'],
    queryFn: async () => (await api.get('/warehouses')).data,
  })

  const warehouseMap = Object.fromEntries(warehouses.map(w => [w.id, w.name]))

  const create = useMutation({
    mutationFn: async () => api.post('/stock/locations', {
      code: form.code.toUpperCase(),
      type: form.type,
      zone: form.zone,
      warehouseId: form.warehouseId || undefined,
      capacity: parseInt(form.capacity) || 1,
      address: form.address || form.code.toUpperCase(),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['locations'] })
      setCreateModal(false)
      setForm({ code: '', type: 'pallet', zone: 'external', warehouseId: '', capacity: '1', address: '' })
    },
  })

  const update = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => api.patch(`/stock/locations/${id}`, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['locations'] }); setEditModal(null) },
  })

  const filtered = filterWarehouse
    ? locations.filter(l => l.warehouseId === filterWarehouse)
    : locations

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Localizações</h2>
          <p className="text-sm text-gray-500">{locations.length} endereços cadastrados</p>
        </div>
        <Button size="sm" onClick={() => setCreateModal(true)}><Plus className="h-4 w-4" /> Nova localização</Button>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm text-gray-600">Armazém:</label>
        <select className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
          value={filterWarehouse} onChange={e => setFilterWarehouse(e.target.value)}>
          <option value="">Todos</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <span className="text-sm text-gray-400">{filtered.length} resultados</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Código</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Tipo</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Zona</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Armazém</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Capacidade</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Status</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((loc) => (
                    <tr key={loc.id} className={`hover:bg-gray-50 ${!loc.active ? 'opacity-50' : ''}`}>
                      <td className="px-5 py-3 font-mono font-semibold text-gray-900">{loc.code}</td>
                      <td className="px-5 py-3 text-gray-600">{typeLabel[loc.type] ?? loc.type}</td>
                      <td className="px-5 py-3 text-gray-600">{zoneLabel[loc.zone] ?? loc.zone}</td>
                      <td className="px-5 py-3 text-gray-600">{loc.warehouseId ? warehouseMap[loc.warehouseId] ?? '—' : '—'}</td>
                      <td className="px-5 py-3 text-right text-gray-600">{loc.capacity}</td>
                      <td className="px-5 py-3">
                        <Badge variant={loc.active ? 'success' : 'danger'}>{loc.active ? 'Ativo' : 'Inativo'}</Badge>
                      </td>
                      <td className="px-5 py-3">
                        <Button variant="ghost" size="sm" onClick={() => setEditModal(loc)}>Editar</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Criar */}
      <Modal title="Nova localização" open={createModal} onClose={() => setCreateModal(false)} size="sm">
        <div className="space-y-4">
          <Input label="Código * (ex: A-06-1)" value={form.code}
            onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
            placeholder="A-06-1" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
                <option value="pallet">Palete</option>
                <option value="shelf">Prateleira</option>
                <option value="floor">Piso</option>
                <option value="staging">Staging</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Zona</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                value={form.zone} onChange={e => setForm(p => ({ ...p, zone: e.target.value }))}>
                <option value="external">Externo</option>
                <option value="internal">Interno</option>
              </select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Armazém</label>
            <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              value={form.warehouseId} onChange={e => setForm(p => ({ ...p, warehouseId: e.target.value }))}>
              <option value="">Sem armazém</option>
              {warehouses.filter(w => w.type === 'storage').map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </div>
          <Input label="Capacidade" type="number" value={form.capacity}
            onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))} />
          {create.error && <p className="text-sm text-red-600">{(create.error as any)?.response?.data?.detail ?? 'Erro'}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setCreateModal(false)}>Cancelar</Button>
            <Button loading={create.isPending} disabled={!form.code} onClick={() => create.mutate()}>Criar</Button>
          </div>
        </div>
      </Modal>

      {/* Editar */}
      <Modal title={`Editar — ${editModal?.code}`} open={!!editModal} onClose={() => setEditModal(null)} size="sm">
        {editModal && (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Armazém</label>
              <select className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                defaultValue={editModal.warehouseId ?? ''}
                onChange={e => setEditModal(p => p ? { ...p, warehouseId: e.target.value || null } : p)}>
                <option value="">Sem armazém</option>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="h-4 w-4" checked={editModal.active}
                onChange={e => setEditModal(p => p ? { ...p, active: e.target.checked } : p)} />
              <span className="text-sm text-gray-700">Ativo</span>
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setEditModal(null)}>Cancelar</Button>
              <Button loading={update.isPending}
                onClick={() => update.mutate({ id: editModal.id, data: { active: editModal.active, warehouseId: editModal.warehouseId } })}>
                Salvar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
